import * as jsonpatch from 'fast-json-patch/index.mjs';
import _ from 'lodash';

import store from '@/store';
import { AttributeItem } from '@/store/modules/staticData/staticDataTypes';

import {
  ARServiceItem,
  AirRoadBookingProduct,
  AirRoadBookingProductsToBook,
  AirRoadJobPackageData,
} from '@/models/airRoad';
import {
  ARServicesDeliveryList,
  ARServicesPickupList,
  statesTimeZonesList,
  timeZoneDefault,
  zammadTicketRfcStatuses,
} from '@/utils/PortalConstants';
import { AxiosResponse, isCancel } from 'axios';
import { JobTicketDashboardItem, JobTicketListItem, JobTicketResponce } from '@/models/jobTicket';
import { User, UserDataAdvancedJB } from '@/models/main';
import { format, utcToZonedTime } from 'date-fns-tz';

import { JobComplianceData } from '@/models/JobComplianceTypes';
import { SchedulerUnscheduledStop } from '@/models/scheduler';
import { clearPhoneNumber } from './InputProcessingUtilities';
import { getProfile } from '@/api';
import { sendError } from '@/helpers/sentry';
import { splitToBaseCatalog } from '@/utils/PortalConstants';

/**
 * Show a default browser download menu with the blob from Axios response
 * First tries to get the name from Content-Disposition header,
 * else tries to get file extension from Content-Type header
 * @param response
 */
export function blobDownloader(response: AxiosResponse): void {
  let fileName;
  const contentType = response.headers['content-type'];
  const blob = new Blob([response.data], { type: contentType });
  const disposition = response.headers['content-disposition'];
  if (disposition && disposition.indexOf('attachment') !== -1) {
    // Regex from https://stackoverflow.com/a/40940790
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
  } else {
    const contentRegex = /\/(?<ext>\w{3})/;
    const matches = contentRegex.exec(contentType as any);
    if (matches != null && matches[1]) {
      fileName = 'kkts_report.' + matches[1];
    }
  }
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', fileName);
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

export function sortByValue(array: SchedulerUnscheduledStop[] | null, value: string): SchedulerUnscheduledStop[] {
  if (array) {
    const sortedArray = array.sort((a, b) => {
      if (a[value] < b[value]) {
        return -1;
      }
      if (a[value] > b[value]) {
        return 1;
      }
      return 0;
    });
    return sortedArray;
  } else {
    return [];
  }
}

export function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function checkTicketIsRfc(ticket: JobTicketListItem | JobTicketResponce | JobTicketDashboardItem): boolean {
  if (!!ticket.is_rfc && ticket.state && zammadTicketRfcStatuses.indexOf(ticket.state.toUpperCase()) >= 0) {
    return true;
  }
  return false;
}

export function isNumeric(value): boolean {
  return /^-?\d+$/.test(value);
}

export function validateEmail(email: string): boolean {
  const regex = /^([\w-\\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return regex.test(email);
}

export function validateEmails(emails): boolean {
  const result = emails.replace(/[ ,]+/g, ',').replace(/\s/g, '').split(/,|;/);
  for (let i = 0; i < result.length; i++) {
    if (!validateEmail(result[i])) {
      return false;
    }
  }
  return true;
}

export function generateUserEmailName(user: User): string {
  let userName = '';
  if (user.first_name) {
    userName = user.first_name;
  }
  if (user.last_name) {
    userName += ' ' + user.last_name;
  }
  if (!user.first_name && !user.last_name) {
    userName = user.username;
  }
  return userName;
}

export function extractEmails(text) {
  return text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi);
}

export function cleanEmailString(emailsToFilter: string[], emailsToDelete: string[]): string[] {
  const toSet = new Set(emailsToDelete);
  const toFiltred = [...new Set(emailsToFilter)].filter(email => {
    return !toSet.has(email);
  });
  return toFiltred;
}

// Allows numbers, space, dash and brackets
export const phonePattern = '^(?+?[d(-s)]+$';

export function buildPackedProducts(productsPacked: AirRoadJobPackageData[]): AirRoadBookingProductsToBook[] {
  const productsToBook: AirRoadBookingProductsToBook[] = [];
  productsPacked.map(product => {
    const items: AirRoadBookingProduct[] = [];
    product.items.map(item => {
      if (item.extra) {
        items.push({
          extra: true,
        });
      } else {
        items.push({
          product_id: item._id,
          quantity: item.quantity,
        });
      }
    });
    productsToBook.push({
      weight: product.weightIsEdited ? Number(product.weightEditedValue) : Number(product.weight),
      volume: product.value,
      item_type: product.type,
      products: items,
    });
  });
  return productsToBook;
}

export function getARServiceData(id: number): ARServiceItem | null {
  const ARServicesList = { ...ARServicesDeliveryList, ...ARServicesPickupList };
  const option = Object.values(ARServicesList).find(x => x.specialOptionId === id);
  if (option) {
    return option;
  }
  return null;
}

function getChangedStopNumbers(diff: Array<{ op: string; path: string; value: string }>): number[] {
  const changedStops = new Set<number>();
  diff.forEach(item => {
    if (item.path.includes('/stops/') && (item.path.includes('/time_earliest') || item.path.includes('/time_latest'))) {
      const stopNumber = parseInt(item.path.split('/stops/')[1], 10);
      changedStops.add(stopNumber);
    }
  });
  return Array.from(changedStops);
}

function getTargetStopNumber(changedStops: number[], totalStops: number): number {
  if (totalStops === 3 && !changedStops.includes(0)) {
    return 1;
  }
  return 0;
}

export function checkJobDatesChanged(formDataSaved: JobComplianceData, formData: JobComplianceData): [boolean, number] {
  const diff = jsonpatch.compare(formDataSaved, formData);
  const changedStops = getChangedStopNumbers(diff);
  const targetStop = getTargetStopNumber(changedStops, formData.stops.length);
  const hasDateChanged = diff.some(item => {
    if (item.path.includes('/time_earliest') || item.path.includes('/time_latest')) {
      const isEarliest = !!item.path.includes('/time_earliest');
      const savedDateValue = formDataSaved.stops[targetStop][`time_${isEarliest ? 'earliest' : 'latest'}`];
      const stopState = formDataSaved.stops[targetStop].state;
      const stopTimeZone = statesTimeZonesList[stopState].timezone || timeZoneDefault;
      const currentDateValueFormatted = format(utcToZonedTime(new Date(savedDateValue), stopTimeZone), 'yyyy-MM-dd');
      const savedDateValueFormatted = format(utcToZonedTime(new Date(item.value), stopTimeZone), 'yyyy-MM-dd');
      return currentDateValueFormatted !== savedDateValueFormatted;
    }
  });
  return [hasDateChanged, targetStop];
}

/**
 * Fills in missing fields in userData from the User object.
 * If the user object doesn't have customerCode, username, phone_number,
 * ref1_prompt, ref2_prompt, or ref3_prompt, it fetches the missing fields
 * from the user profile.
 * @param userData - the data to be filled in
 * @param user - the user to get the data from
 * @returns a new UserData object with the filled in fields
 */
export async function getUserDataForNonStaffUsers(
  userData: UserDataAdvancedJB,
  user: User,
): Promise<UserDataAdvancedJB> {
  const newUserData: UserDataAdvancedJB = { ...userData };
  if (
    !user.customerCode ||
    !user.username ||
    !user.phone_number ||
    !user.ref1_prompt ||
    !user.ref2_prompt ||
    !user.ref3_prompt
  ) {
    try {
      await getProfile().then((res: User) => {
        // If user has first name put it into caller and slice to 16 as RH limit
        // else use username
        if (res.first_name) {
          newUserData.caller = (res.first_name + ' ' + res.last_name).slice(0, 16);
        } else {
          newUserData.caller = res.username.split('@')[0].slice(0, 16);
        }
        if (res.phone_number) {
          newUserData.phone_number = clearPhoneNumber(res.phone_number);
        }
      });
    } catch (err) {
      if (!isCancel(err)) {
        sendError(err, 'getUserDataFornNonStaffUsers');
      }
    }
  } else {
    newUserData.caller = user.username.split('@')[0].slice(0, 16);
    newUserData.phone_number = clearPhoneNumber(user.phone_number);
  }
  newUserData.customerCode = user.customerCode;
  return newUserData;
}

/**
 * Get attribute icon from store or return default icon if not found or icon is null
 */
export function getAttributeIcon(attrCode: string): string {
  const attributes = store.getters['moduleStaticData/getAttributes'] as AttributeItem[];
  const attribute = attributes?.find(attr => attr.code === attrCode);
  return attribute?.icon || 'mdi-alert-circle';
}

/**
 * Get attribute description from store
 */
export function getAttributeDescription(attrCode: string): string {
  const attributes = store.getters['moduleStaticData/getAttributes'] as AttributeItem[];
  const attribute = attributes?.find(attr => attr.code === attrCode);
  return attribute?.description || 'Unknown attribute';
}

/**
 * Check if attribute exists in store
 */
export function hasAttribute(attrCode: string): boolean {
  const attributes = store.getters['moduleStaticData/getAttributes'] as AttributeItem[];
  return attributes?.some(attr => attr.code === attrCode) || false;
}

/**
 * Get base (warehouse) name by number (`split_base` field)
 */
export function getBaseName(baseNumber: number | null | undefined): string {
  if (!baseNumber || baseNumber === 0) {
    return '';
  }
  const base = splitToBaseCatalog.find(b => b.number === baseNumber);
  return base?.name || `${baseNumber}`;
}

/**
 * Format camelCase or snake_case keys into readable labels
 */
const formatLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Parse `special_instructions` (JobDetailed, JobEdit) array into simple key-value pairs for display
 */
export const parseSpecialInstructions = (
  specialInstructions: { [key: string]: unknown }[],
): { label: string; value: string }[] => {
  if (!Array.isArray(specialInstructions) || specialInstructions.length === 0) {
    return [];
  }

  const parsed: { label: string; value: string }[] = [];

  specialInstructions.forEach(item => {
    // Special handling for objects with 'type' and 'text' properties
    if (item.type && item.text && typeof item.type === 'string' && typeof item.text === 'string') {
      parsed.push({
        label: item.type,
        value: item.text,
      });
      return;
    }
    // Default handling for other objects
    Object.keys(item).forEach(key => {
      const value = item[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && value !== null) {
          // Handle nested objects like service_level
          Object.keys(value as Record<string, unknown>).forEach(nestedKey => {
            const nestedValue = (value as Record<string, unknown>)[nestedKey];
            if (nestedValue !== undefined && nestedValue !== null) {
              parsed.push({
                label: formatLabel(`${key} ${nestedKey}`),
                value: String(nestedValue),
              });
            }
          });
        } else {
          parsed.push({
            label: formatLabel(key),
            value: String(value),
          });
        }
      }
    });
  });

  return parsed;
};

export function sortTableData<T>(data: T[], field: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return _.orderBy(
    data,
    [
      item => {
        const value = item[field];
        // Handle Odoo relation fields (arrays with [id, name])
        if (Array.isArray(value) && value.length === 2) {
          return value[1] || '';
        }
        return value;
      },
    ],
    [direction],
  );
}
